import { useCallback, useMemo, useRef, useState } from "react";
import {
  Box,
  EditableValueCell,
  Input,
  InputGroup,
  Text,
  type EditableValueCellAction,
  type EditableValueCellEditor,
  type Omittable,
} from "@salestrak/ui";
import {
  CUSTOM_FIELD_MAX_LENGTHS,
  dataToField,
  fieldDto,
  formatPhoneNumber,
  isValidEmailText,
  isValidPhoneValue,
  isValidUrlText,
  normalizePhoneNumber,
  normalizeUrlValue,
  urlDisplayText,
} from "@salestrak/custom-fields";
import { PhoneInlineInput } from "#app/components/custom-fields/phone-inline-input";
import { InvalidPhoneMarker } from "#app/components/custom-fields/invalid-phone-marker";
import {
  blurActiveElement,
  buildCopyValueAction,
  getFieldTypeDescriptor,
  getProtocolHref,
  getValueActionText,
} from "#app/components/fields/field-type-descriptors";
import { UrlFavicon } from "#app/components/fields/url-favicon";
import { NONE_OPTION_LABEL } from "#app/lib/custom-field-utils";
import {
  PROPERTY_CONTROL_HEIGHT,
  PROPERTY_VALUE_EDIT_INSET_CLASS,
  PROPERTY_VALUE_REST_INSET_CLASS,
  TABLE_VALUE_INSET_CLASS,
} from "#app/components/fields/property-value-inset";
import { useFlushOnUnmount } from "#app/lib/hooks/use-flush-on-unmount";
import type { CustomFieldType } from "#app/lib/data";

/**
 * The built-in cell for a scalar text-like PROPERTY — `text`, `email`, `phone`
 * or `url` — sibling to {@link AddressValueCell}. Same `EditableValueCell`
 * composition a custom field of the same type gets: same activation, same touch
 * reveal, same hover value-actions row (call/email/open, copy, clear), same
 * outside-click dismissal, same invalid-commit veto. It reads its action set,
 * noun and protocol verb from the type's descriptor, so a surface showing a
 * built-in Email property beside an email custom field cannot present two
 * editing UXs.
 *
 * It takes the descriptor KEY rather than a `CustomFieldDef` because a built-in
 * property has no definition row — fabricating one to reach the shared cell is
 * the fork this system exists to prevent. Only the commit differs from a custom
 * field: `onCommit` writes the property, not the values map.
 */

export type TextValueCellProps = Omittable<{
  /** Descriptor key. Text-likes only — an overlay-editor type (`select`,
   *  `date`, `member`) needs its own composition, not this one. */
  type: Extract<CustomFieldType, "text" | "email" | "phone" | "url">;
  value?: string | null;
  /** Receives the trimmed value, or null once it is emptied. */
  onCommit: (next: string | null) => void;
  /** Names the value in the cell's edit affordance ("Edit website"). Defaults
   *  to the descriptor's own noun. */
  label?: string;
  /** The editor input's placeholder. The rest state never shows it — an
   *  empty value reads {@link emptyLabel}. */
  placeholder?: string;
  /** Rest-state text of an empty value. Defaults to the "\u2014" every empty
   *  table cell wears; a property row passes its ghost prompt
   *  ("Add email..."). */
  emptyLabel?: string;
  /** Text tone for an empty value. */
  emptyTextVariant?: React.ComponentProps<typeof Text>["variant"];
  /** Rejects a commit and holds the cell open, the way a custom field's
   *  value schema does. Return null when the text is acceptable. */
  validate?: (next: string) => string | null;
  /** View/comment access tiers: display, copy and protocol only. */
  readOnly?: boolean;
  /** Cell height in px. Defaults to the 32px property control rung on the property
   *  surface; a table cell fills its host (the row minus the 2px ring inset). */
  height?: number;
  /** Where the text sits. `cell` is the table grammar: 8px text inset, the
   *  hover-outline ring (the edit promise). `property` is the property
   *  panel's: the 12px inset the sibling selector Buttons put their labels
   *  at, and a full-width ghost fill on hover instead of the ring.
   *  `field` is the FORM grammar: the cell stands where an `Input` would, in a
   *  settings row among real Inputs, so it wears the library `InputGroup`
   *  chrome — the frame paints the border, the focus ring and the invalid
   *  edge (read off the editor's own input), the cell paints no ring or wash
   *  of its own, and the text sits at the Input's 11px inset in both states.
   *  An empty value reads `—` in the frame the way the sibling select does,
   *  and the click opens the editor in place. */
  inset?: "cell" | "property" | "field";
  /** Table hosts pass false — a row clips the end bleed square. */
  bleedEnd?: boolean;
}>;

export function TextValueCell({
  type,
  value,
  onCommit,
  label,
  placeholder,
  emptyLabel,
  emptyTextVariant = "faint",
  validate,
  readOnly = false,
  height: heightProp,
  inset = "cell",
  bleedEnd = true,
}: TextValueCellProps) {
  const isProperty = inset === "property";
  const isField = inset === "field";
  // Both borderless-at-rest grammars and the framed one pad the value to the
  // Input's text edge; only the table cell sits at 8px.
  const editorInset = inset === "cell" ? "cell" : "property";
  // The framed cell fills the Input's 32px outer rung inside the frame's 1px
  // border, so a settings row's phone stands level with the inputs above it.
  const height =
    heightProp ??
    (isField
      ? PROPERTY_CONTROL_HEIGHT - 2
      : isProperty
        ? PROPERTY_CONTROL_HEIGHT
        : undefined);
  const [isActive, setIsActive] = useState(false);
  const [overlayOpen, setOverlayOpen] = useState(false);

  const descriptor = getFieldTypeDescriptor(type);
  const text = value?.trim() ?? "";
  const hasValue = text.length > 0;
  // Phones store E.164 and read formatted; they EDIT through the shared phone
  // editor's region + national pair (`dataToField`), and its commit folds the
  // pair back to E.164 (`fieldDto`) — the same round trip a phone custom field
  // makes. URLs store with their scheme and read without it, but EDIT as
  // stored — the completed `https://…` reopens whole, so the draft is exactly
  // what will persist.
  const isUrl = type === "url";
  const isPhone = type === "phone";
  const isEmail = type === "email";
  const displayText =
    isPhone && hasValue
      ? formatPhoneNumber(text)
      : isUrl && hasValue
        ? urlDisplayText(text)
        : text;
  // A phone saved before invalid numbers were refused can still fail to
  // parse, so the rest state flags it: red text plus the marker.
  const flaggedPhone = isPhone && hasValue && !isValidPhoneValue(text);
  const noun = label ?? descriptor.actionNoun;

  const valueActionText = useMemo(
    () => getValueActionText(type, value, text),
    [text, type, value],
  );
  const protocolHref = useMemo(
    () => (hasValue ? getProtocolHref(type, valueActionText) : null),
    [hasValue, type, valueActionText],
  );

  const handleCommit = useCallback(
    (next: unknown) => {
      const nextText = typeof next === "string" ? next.trim() : "";
      if (!nextText) {
        onCommit(null);
        return;
      }
      // Phones persist E.164 — the same normalization the custom-field DTO
      // applies — so every display site can format them. URLs persist with
      // a scheme, `https://` completing one typed without. Text that does not
      // parse persists as typed.
      onCommit(
        isPhone
          ? normalizePhoneNumber(nextText)
          : isUrl
            ? normalizeUrlValue(nextText)
            : nextText,
      );
    },
    [isPhone, isUrl, onCommit],
  );

  // A URL, email or phone is gated the way the matching custom field is —
  // the same predicate its live ring reads — ahead of whatever the caller
  // checks. A refused phone keeps the editor open with the typed digits.
  // Text validates only when the caller asks.
  const validateText = useMemo<((next: string) => string | null) | undefined>(() => {
    if (isUrl) {
      return (next) =>
        isValidUrlText(next) ? (validate?.(next) ?? null) : "Invalid URL";
    }
    if (isEmail) {
      return (next) =>
        isValidEmailText(next) ? (validate?.(next) ?? null) : "Invalid email";
    }
    return validate;
  }, [isEmail, isUrl, validate]);

  const handleValidate = useCallback(
    (next: unknown): string | null => {
      // The phone editor emits its form-space pair, not text.
      if (isPhone) {
        return isValidPhoneValue(next)
          ? (validate && typeof next === "string" ? validate(next.trim()) : null)
          : "Invalid phone number";
      }
      if (!validateText) return null;
      const nextText = typeof next === "string" ? next.trim() : "";
      // An emptied property is a clear, never a validation failure — the same
      // way every validator here treats blank input as valid.
      return nextText ? validateText(nextText) : null;
    },
    [isPhone, validate, validateText],
  );

  // The protocol opens in a button of its own; clicking the cell always edits.
  // No overlay opens, so this must not report through `onOverlayChange` —
  // nothing would emit the matching close and the highlight would stick.
  const handleOpenProtocol = useCallback(() => {
    if (!protocolHref || typeof window === "undefined") return;
    blurActiveElement();
    if (type === "url") {
      window.open(protocolHref, "_blank", "noopener,noreferrer");
    } else {
      window.location.href = protocolHref;
    }
  }, [protocolHref, type]);

  const actions = useMemo<EditableValueCellAction[]>(() => {
    if (!hasValue) return [];
    const list: EditableValueCellAction[] = [];
    // Walked in the descriptor's own order so this cell and a custom-field cell
    // of the same type always offer the same actions in the same places.
    for (const action of descriptor.actions) {
      switch (action) {
        case "open":
          if (!protocolHref) break;
          list.push({
            key: "open",
            icon: descriptor.protocolIcon ?? "external-link",
            label: `${descriptor.protocolVerb ?? "Open"} ${noun}`,
            tooltip:
              descriptor.protocolTooltip ?? descriptor.protocolVerb ?? "Open",
            onSelect: handleOpenProtocol,
          });
          break;
        case "copy":
          list.push(buildCopyValueAction(noun, () => valueActionText));
          break;
        case "clear":
          list.push({
            key: "clear",
            icon: "x",
            label: `Clear ${noun}`,
            tooltip: "Clear",
            onSelect: (cell) => cell.overwrite(null),
          });
          break;
      }
    }
    return readOnly ? list.filter((a) => a.key !== "clear") : list;
  }, [
    descriptor,
    handleOpenProtocol,
    hasValue,
    noun,
    protocolHref,
    readOnly,
    valueActionText,
  ]);

  const display = (
    <Box
      fill="flex"
      direction="row"
      align="center"
      gap="xs"
      // The framed cell already sits inside the frame's 1px border, so its
      // rest text pads the editing inset — the same 12px from the frame's
      // outer edge the sibling Inputs put their text at.
      className={
        isField
          ? PROPERTY_VALUE_EDIT_INSET_CLASS
          : isProperty
            ? PROPERTY_VALUE_REST_INSET_CLASS
            : TABLE_VALUE_INSET_CLASS
      }
    >
      {isUrl && hasValue && <UrlFavicon url={text} />}
      <Box fill={flaggedPhone ? undefined : "flex"} shrink minWidth={0}>
        <Text
          size="sm"
          truncate={1}
          variant={
            flaggedPhone
              ? "destructive"
              : hasValue
                ? undefined
                : emptyTextVariant
          }
        >
          {hasValue ? displayText : (emptyLabel ?? NONE_OPTION_LABEL)}
        </Text>
      </Box>
      {flaggedPhone && <InvalidPhoneMarker />}
    </Box>
  );

  // The live ring reads the same predicate the commit gate does, so a ring
  // that has cleared can never be refused at blur.
  const draftError = isUrl
    ? (draft: string) =>
        !draft.trim() || isValidUrlText(draft) ? null : "Invalid URL"
    : isEmail
      ? (draft: string) =>
          !draft.trim() || isValidEmailText(draft) ? null : "Invalid email"
      : undefined;

  const editor: EditableValueCellEditor = {
    kind: "inline",
    render: ({ commit, deactivate, invalid, reportDraftError }) =>
      isPhone ? (
        // The custom-field phone editor over the stored E.164 string. It
        // commits on blur and on a country change, then schedules its own
        // deactivation (tolerating focus in the country popover); the pair
        // it emits folds back to E.164 here. `invalid` lands on the input as
        // `aria-invalid`, which is what a framing `InputGroup` reads.
        <PhoneInlineInput
          autoFocus
          inset={editorInset}
          invalid={invalid}
          onChangeValue={(pair) => commit(fieldDto("phone", pair))}
          onDeactivate={deactivate}
          onDraftErrorChange={reportDraftError}
          value={dataToField("phone", text).fieldValue}
        />
      ) : (
        <PropertyTextInput
          ariaLabel={`Edit ${noun}`}
          commit={commit}
          deactivate={deactivate}
          inset={editorInset}
          invalid={invalid}
          draftError={draftError}
          onDraftErrorChange={reportDraftError}
          placeholder={placeholder}
          selectOnFocus={isUrl}
          type={type}
          value={text}
        />
      ),
  };

  const cell = (
    <EditableValueCell
      actions={actions}
      // The frame clips its own radius, like a table row.
      bleedEnd={isField ? false : bleedEnd}
      display={display}
      editor={readOnly ? undefined : editor}
      hasValue={hasValue}
      height={height}
      highlighted={overlayOpen || isActive}
      isActive={isActive}
      onActivate={() => setIsActive(true)}
      onCommit={handleCommit}
      onDeactivate={() => setIsActive(false)}
      onOverlayChange={setOverlayOpen}
      // The frame paints the editing state (focus ring, invalid edge) on its
      // own chrome, so the cell reserves no border of its own.
      outline={!isField}
      readOnly={readOnly}
      // Property rows hover as a full-width ghost fill, like the selector
      // triggers beside them; table cells keep the outline ring; the
      // framed cell leaves hover to its frame, like the other Inputs beside it.
      restHover={isField ? "none" : isProperty ? "fill" : "outline"}
      triggerLabel={`Edit ${noun}`}
      validate={validateText || isPhone ? handleValidate : undefined}
      value={value ?? null}
    />
  );

  if (isField) {
    return (
      // The Input's own chrome around the cell: border, fill, the focus ring
      // once the editor's input takes focus, the destructive edge while its
      // draft does not parse. The rest trigger is a button, not an input, so
      // its keyboard focus is ringed here too — the frame is the one edge.
      <InputGroup className="has-[button:focus-visible]:border-ring has-[button:focus-visible]:ring-[3px]">
        {cell}
      </InputGroup>
    );
  }

  return (
    // Table hosts float the ring 2px inside the row and the cell fills the
    // rest (see AddressValueCell); property rows are borderless at rest and
    // fill their 32px row exactly.
    <Box
      fill="flex"
      minWidth={0}
      direction="row"
      align="stretch"
      paddingY={isProperty ? undefined : "2xs"}
    >
      {cell}
    </Box>
  );
}

const INPUT_MODE: Record<string, "text" | "email" | "tel" | "url"> = {
  email: "email",
  phone: "tel",
  url: "url",
};

/**
 * Uncontrolled while editing, committing on blur — the same contract the
 * custom-field text renderer keeps. A controlled input would re-render the
 * whole host on every keystroke, and the property panel hosts a dozen of these.
 * The flush hook covers the case where the cell unmounts (dialog closed, row
 * scrolled out) before blur fires, so a typed value is never silently dropped.
 */
function PropertyTextInput({
  ariaLabel,
  commit,
  deactivate,
  draftError,
  inset = "cell",
  invalid,
  onDraftErrorChange,
  placeholder,
  selectOnFocus = false,
  type,
  value,
}: Omittable<{
  /** The host cell's surface — picks the input's text inset (see
   *  property-value-inset.ts). */
  inset?: "cell" | "property";
  ariaLabel: string;
  commit: (next: unknown) => void;
  deactivate: () => void;
  /** Names why the draft is not yet acceptable, `null` once it is. The host
   *  paints its editing ring red from it; the draft still commits on blur. */
  draftError?: (draft: string) => string | null;
  /** The cell's surfaced error, stamped on the input as `aria-invalid` for a
   *  frame (`InputGroup`) that paints the invalid edge off its input. */
  invalid?: boolean;
  onDraftErrorChange?: (message: string | null) => void;
  placeholder?: string;
  /** Offer the whole value for replacement when editing opens — for values
   *  the commit completed beyond what was typed (a URL's scheme). */
  selectOnFocus?: boolean;
  type: string;
  value: string;
}>) {
  const draft = useRef(value);
  const markFlushed = useFlushOnUnmount(draft, commit);
  // Set once the draft is settled by a key (Enter committed it, Escape
  // discarded it) so the blur that follows the unmount does not commit again.
  const settledRef = useRef(false);
  const onDraftErrorChangeRef = useRef(onDraftErrorChange);
  onDraftErrorChangeRef.current = onDraftErrorChange;

  return (
    <Input
      aria-invalid={invalid || undefined}
      aria-label={ariaLabel}
      autoFocus
      bare
      className={`w-full min-w-0 bg-transparent ${
        inset === "property"
          ? PROPERTY_VALUE_EDIT_INSET_CLASS
          : TABLE_VALUE_INSET_CLASS
      } text-sm leading-5 text-foreground placeholder:text-muted-foreground/60`}
      defaultValue={value}
      inputMode={INPUT_MODE[type] ?? "text"}
      maxLength={CUSTOM_FIELD_MAX_LENGTHS[type]}
      onBlur={() => {
        if (settledRef.current) return;
        markFlushed();
        commit(draft.current);
        deactivate();
      }}
      onChange={(event) => {
        draft.current = event.target.value;
        // A rejected Enter commit holds the editor open (the cell vetoes the
        // deactivation); the next keystroke reopens the blur-commit path.
        settledRef.current = false;
        if (draftError) onDraftErrorChangeRef.current?.(draftError(draft.current));
      }}
      onFocus={(event) => {
        if (selectOnFocus) event.currentTarget.select();
      }}
      onKeyDown={(event) => {
        // Enter commits like blur does; Escape discards the draft — the
        // flush is marked done so the unmount does not commit it after all.
        if (event.key === "Enter") {
          event.preventDefault();
          settledRef.current = true;
          markFlushed();
          commit(draft.current);
          deactivate();
        } else if (event.key === "Escape") {
          settledRef.current = true;
          markFlushed();
          deactivate();
        }
      }}
      placeholder={placeholder}
      type={INPUT_MODE[type] ?? "text"}
    />
  );
}
